import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import { getDoctorFilterParams } from '../../utils/doctorFilter';
import {
  Card,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  Table,
  InputGroup,
  Form,
  Modal,
  Pagination
} from 'react-bootstrap';
import { FaSearch, FaFileMedical, FaTrash, FaArchive } from 'react-icons/fa';

// CSS for report pagination
const paginationStyle = {
  wrapper: {
    margin: '15px 0',
    padding: '8px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  pageCount: {
    color: '#6c757d',
    fontWeight: '500'
  },
  pagination: {
    marginBottom: '0'
  }
};

const Reports = () => {
  const { user } = useContext(AuthContext);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredReports, setFilteredReports] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [reportToArchive, setReportToArchive] = useState(null);
  
  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [reportsPerPage] = useState(10);

  // Debug: Log user permissions
  useEffect(() => {
    console.log('Reports - User:', user);
    console.log('Reports - User Role:', user?.role);
    console.log('Reports - User Permissions:', user?.permissions);
    console.log('Reports - viewReportAction:', user?.permissions?.viewReportAction);
    console.log('Reports - editReportAction:', user?.permissions?.editReportAction);
    console.log('Reports - archiveReportAction:', user?.permissions?.archiveReportAction);
    console.log('Reports - deleteReportAction:', user?.permissions?.deleteReportAction);
  }, [user]);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        console.log("Fetching reports");
        const filterParams = getDoctorFilterParams(user);
        const res = await axios.get('/api/reports.php', { params: filterParams });
        console.log("Reports received:", res.data.length);
        
        // Debug log a sample report to verify structure
        if (res.data.length > 0) {
          console.log("Sample report structure:", {
            id: res.data[0].id,
            patientId: res.data[0].patientId,
            doctorId: res.data[0].doctorId,
            hasPatient: !!res.data[0].patient,
            hasDoctor: !!res.data[0].doctor
          });
        }
        
        setReports(res.data);
        setFilteredReports(res.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching reports:", err);
        if (err.response) {
          console.error("Response data:", err.response.data);
          console.error("Response status:", err.response.status);
          setError(`Failed to load reports: ${err.response.data.msg || err.message}`);
        } else {
          setError(`Failed to load reports: ${err.message}`);
        }
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredReports(reports);
    } else {
      const lowercaseSearchTerm = searchTerm.toLowerCase();
      
      const filtered = reports.filter(report => {
        // Handle potentially missing properties safely
        const patientName = report.patient?.name?.toLowerCase() || '';
        const patientId = report.patient?.patientId?.toLowerCase() || '';
        const doctorName = report.doctor?.name?.toLowerCase() || '';
        const diagnosisText = report.diagnosis?.toLowerCase() || '';
        
        return patientName.includes(lowercaseSearchTerm) ||
               patientId.includes(lowercaseSearchTerm) ||
               doctorName.includes(lowercaseSearchTerm) ||
               diagnosisText.includes(lowercaseSearchTerm);
      });
      
      setFilteredReports(filtered);
    }
  }, [searchTerm, reports]);

  // Get current reports for pagination
  const indexOfLastReport = currentPage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;
  const currentReports = filteredReports.slice(indexOfFirstReport, indexOfLastReport);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // When filtering changes, reset to first page
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredReports.length]);

  const handleDeleteClick = (report) => {
    setReportToDelete(report);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!reportToDelete) return;
    
    try {
      await axios.delete(`/api/reports.php?id=${reportToDelete.id}`);
      
      // Remove report from state
      setReports(reports.filter(r => r.id !== reportToDelete.id));
      setShowDeleteModal(false);
      setReportToDelete(null);
    } catch (err) {
      console.error(err);
      setError('Failed to delete report');
    }
  };

  const handleArchiveClick = (report) => {
    setReportToArchive(report);
    setShowArchiveModal(true);
  };

  const handleConfirmArchive = async () => {
    if (!reportToArchive) return;
    
    try {
      await axios.put(`/api/reports.php?id=${reportToArchive.id}`, { isArchived: 1 });
      
      // Remove report from state
      setReports(reports.filter(r => r.id !== reportToArchive.id));
      setShowArchiveModal(false);
      setReportToArchive(null);
    } catch (err) {
      console.error(err);
      setError('Failed to archive report');
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {error}. <Link to="/">Go back to dashboard</Link>
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Reports</h2>
        <div>
          {(user?.role === 'admin' || user?.permissions?.viewArchive) && (
            <Link to="/reports/archive" className="btn btn-secondary me-2">
              <FaArchive className="me-2" /> View Archive
            </Link>
          )}
          {(user?.role === 'admin' || user?.permissions?.newReport) && (
            <Link to="/patients">
              <Button variant="primary">
                <FaFileMedical className="me-2" /> New Report
              </Button>
            </Link>
          )}
        </div>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <InputGroup>
            <Form.Control
              placeholder="Search by patient name, ID, doctor, or diagnosis..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
          </InputGroup>
        </Card.Body>
      </Card>

      {filteredReports.length > 0 ? (
        <>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Report ID</th>
                <th>Patient</th>
                <th>Doctor</th>
                <th>Date</th>
                <th>Diagnosis</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentReports.map(report => (
                <tr key={report.id}>
                  <td className="report-id">{report.reportId || 'N/A'}</td>
                  <td>
                    {report.patientName ? (
                      <>
                        <span className="text-decoration-none">
                          {report.patientName}
                        </span>
                        <div className="text-muted small">ID: {report.patientId}</div>
                      </>
                    ) : (
                      <span>Patient unavailable</span>
                    )}
                  </td>
                  <td>{report.doctorName || 'N/A'}</td>
                  <td>{report.date ? new Date(report.date).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    {report.diagnosis && report.diagnosis.length > 50
                      ? report.diagnosis.substring(0, 50) + '...'
                      : report.diagnosis || 'No diagnosis'}
                  </td>
                  <td>
                    {(user?.role === 'admin' || user?.permissions?.viewReportAction) && (
                      <Link to={`/reports/${report.id}`} className="btn btn-sm btn-info me-2">
                        View
                      </Link>
                    )}
                    {(user?.role === 'admin' || user?.permissions?.editReportAction) && (
                      <Link to={`/reports/edit/${report.id}`} className="btn btn-sm btn-warning me-2">
                        Edit
                      </Link>
                    )}
                    {(user?.role === 'admin' || user?.permissions?.archiveReportAction) && (
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handleArchiveClick(report)}
                        title="Archive Report"
                      >
                        <FaArchive />
                      </Button>
                    )}
                    {(user?.role === 'admin' || user?.permissions?.deleteReportAction) && (
                      <Button 
                        variant="danger" 
                        size="sm" 
                        onClick={() => handleDeleteClick(report)}
                        title="Delete Report"
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {/* Pagination */}
          {filteredReports.length > reportsPerPage && (
            <div className="d-flex flex-column align-items-center mt-3 mb-3" style={paginationStyle.wrapper}>
              <div className="text-center mb-2" style={paginationStyle.pageCount}>
                <small>
                  Showing {indexOfFirstReport + 1}-{Math.min(indexOfLastReport, filteredReports.length)} of {filteredReports.length} reports
                </small>
              </div>
              <Pagination style={paginationStyle.pagination}>
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                />
                
                {Array.from({ length: Math.ceil(filteredReports.length / reportsPerPage) }).map((_, index) => (
                  <Pagination.Item
                    key={index + 1}
                    active={index + 1 === currentPage}
                    onClick={() => paginate(index + 1)}
                  >
                    {index + 1}
                  </Pagination.Item>
                ))}
                
                <Pagination.Next
                  onClick={() => setCurrentPage(prev => 
                    Math.min(prev + 1, Math.ceil(filteredReports.length / reportsPerPage))
                  )}
                  disabled={currentPage === Math.ceil(filteredReports.length / reportsPerPage)}
                />
                <Pagination.Last 
                  onClick={() => setCurrentPage(Math.ceil(filteredReports.length / reportsPerPage))} 
                  disabled={currentPage === Math.ceil(filteredReports.length / reportsPerPage)} 
                />
              </Pagination>
            </div>
          )}
        </>
      ) : (
        <Alert variant="info">
          No reports found. <Link to="/patients">Create a new report</Link>
        </Alert>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this report?
          {reportToDelete?.patient && (
            <p>Patient: <strong>{reportToDelete.patient.name}</strong></p>
          )}
          {reportToDelete?.date && (
            <p>Date: <strong>{new Date(reportToDelete.date).toLocaleDateString()}</strong></p>
          )}
          <p className="mt-2 text-danger">
            <strong>Warning:</strong> This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete Report
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal show={showArchiveModal} onHide={() => setShowArchiveModal(false)}>
        <Modal.Header>
          <Modal.Title>Archive Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to archive this report?
          {reportToArchive?.patient && (
            <p>Patient: <strong>{reportToArchive.patient.name}</strong></p>
          )}
          {reportToArchive?.date && (
            <p>Date: <strong>{new Date(reportToArchive.date).toLocaleDateString()}</strong></p>
          )}
          <p className="mt-2">
            Archived reports will be hidden from the main list but can be accessed through the archive section.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowArchiveModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmArchive}>
            Archive Report
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Reports; 
